import { IconIntroduction } from '@consta/icons/IconIntroduction'
import { IconShape } from '@consta/icons/IconShape'
import { IconTrash } from '@consta/icons/IconTrash'
import type { Props as ButtonProps } from '@consta/uikit/Button'
import { Button } from '@consta/uikit/Button'
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import { withTooltip } from '@consta/uikit/withTooltip'
import { GroupingHeader } from '@core/components'
import { getSimpleTooltipContent } from '@core/core/helpers'
import type { Nullable } from 'common/types'
import type { FC } from 'react'

import { isValidGeometry } from '../helpers'
import { choiceItems } from '../types'
import { ERROR_TEXT } from './constants'
import css from './MapBuildingRightMenu.css'
import type { GeometryMenuProps, onChangeGeometryParams } from './types'
import { XYTextFields } from './XYTextFields'

const buttonProps: ButtonProps = {
  size: 'xs',
  view: 'secondary',
  onlyIcon: true,
}
const xItems = ['Мин Х', 'Макс Х', 'Длина Х']
const yItems = ['Мин Y', 'Макс Y', 'Длина Y']

const ButtonWithTooltip = withTooltip({
  direction: 'upCenter',
  mode: 'mouseover',
  appearTimeout: 200,
})(Button)

export const GeometryMenu: FC<GeometryMenuProps> = ({
  choiceValue,
  formData,
  setChoiceValue,
  updateGeometryData,
}) => {
  const disabledItems = choiceValue === choiceItems[0]
  const disabledProps = {
    disabled: disabledItems,
  }

  const onChangeGeometry = (elem: onChangeGeometryParams) => {
    if (elem.name) {
      updateGeometryData(prev => ({
        ...prev,
        [elem.name]: Number(elem.value),
      }))
    }
  }

  const onChangeGrid = (value: Nullable<string>) => {
    onChangeGeometry({ name: 'xStep', value })
    onChangeGeometry({ name: 'yStep', value })
  }

  const isValid = isValidGeometry(formData)

  return (
    <div className={css.wrapper}>
      <Text size="xs" view="secondary" className={css.sortTypeText}>
        Тип
      </Text>
      <ChoiceGroup
        size="xs"
        view="ghost"
        value={choiceValue}
        items={choiceItems}
        getItemLabel={item => item.name}
        multiple={false}
        name="sortGroup"
        className={css.rightMenuChoiceGroup}
        onChange={setChoiceValue}
      />
      <GroupingHeader className={css.headerMargin} title="Размер и позиция">
        <ButtonWithTooltip
          iconLeft={IconIntroduction}
          tooltipProps={{ content: getSimpleTooltipContent('Импорт настроек из дерева/объекта') }}
          {...buttonProps}
          {...disabledProps}
        />
        <ButtonWithTooltip
          iconLeft={IconShape}
          tooltipProps={{ content: getSimpleTooltipContent('По границам объекта') }}
          {...buttonProps}
          {...disabledProps}
        />
        <ButtonWithTooltip
          size="xs"
          view="ghost"
          iconLeft={IconTrash}
          onlyIcon={true}
          tooltipProps={{ content: getSimpleTooltipContent('Очистить') }}
          {...disabledProps}
        />
      </GroupingHeader>
      <XYTextFields
        labelsArray={xItems}
        disabledProps={disabledProps}
        data={{
          max: {
            name: 'xMax',
            value: formData.xMax,
          },
          min: {
            name: 'xMin',
            value: formData.xMin,
          },
        }}
        isValid={isValid}
        onChangeGeometry={onChangeGeometry}
      />
      <XYTextFields
        labelsArray={yItems}
        disabledProps={disabledProps}
        data={{
          max: {
            name: 'yMax',
            value: formData.yMax,
          },
          min: {
            name: 'yMin',
            value: formData.yMin,
          },
        }}
        isValid={isValid}
        onChangeGeometry={onChangeGeometry}
      />
      <div className={`${css.menuFields} ${css.menuFieldsPadding}`}>
        <Text className={css.textPadding} size="xs" view="secondary">
          Угол R
        </Text>
        <TextField
          size="xs"
          className={css.geometryMenuTextFields}
          {...disabledProps}
          withClearButton={true}
        />
      </div>
      <div className={css.menuFields}>
        <Text className={css.textPadding} size="xs" view="secondary">
          Сетка XY
        </Text>
        <TextField
          size="xs"
          type="number"
          min={0}
          withClearButton={!disabledProps.disabled}
          className={`${css.geometryMenuGridFields} ${css.geometryMenuGridFieldsMargin}`}
          name="xStep"
          value={String(formData.xStep)}
          status={!isValid ? 'alert' : undefined}
          onChange={onChangeGrid}
          {...disabledProps}
        />
        <TextField
          size="xs"
          type="number"
          min={0}
          withClearButton={!disabledProps.disabled}
          className={css.geometryMenuGridFields}
          name="yStep"
          value={String(formData.yStep)}
          status={!isValid ? 'alert' : undefined}
          onChange={onChangeGrid}
          {...disabledProps}
        />
      </div>
      {!isValid && (
        <div className={css.errorText}>
          <Text align="right" size="xs">
            {ERROR_TEXT}
          </Text>
        </div>
      )}
    </div>
  )
}
